import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import type {Organization} from 'crqlar-react-common/dist/models/organization/organization';
import type {User} from 'crqlar-react-common/dist/models/identity/user.model';

export interface AssistantRouteMetadata {
    currentPath: string;
    routeGroup?: string;
    routeName?: string;
    entityType?: string;
    entityId?: string;
    isDetailView: boolean;
    activeTab?: string;
}

export interface AssistantUserContext {
    userType?: string;
    interfaceLanguage?: string;
    permittedAppIds?: string[];
    billingAccess?: boolean;
    marketplaceAccess?: boolean;
    settingsAccess?: boolean;
}

export interface AssistantOrganizationContext {
    id?: string;
    name?: string;
    primaryLanguage?: string;
    supportedLanguages?: string[];
    currencyCode?: string;
    numberOfRooms?: number;
}

export interface AssistantContextSnapshot {
    currentPath: string;
    metadata: AssistantRouteMetadata;
    user?: AssistantUserContext;
    organization?: AssistantOrganizationContext;
}

// `- Crqlar` is what DocumentTitleManager appends; the others cover any page that sets its own title.
const APP_NAME_SUFFIX = /\s*[-–—|]\s*Crqlar\s*$/;

/**
 * The human-readable name of the page the customer is on, for the support team's Slack context line.
 *
 * Sourced from `document.title`, which the whole dashboard already funnels through
 * `layout/DocumentTitleManager` (static route table + `useDocumentTitleOverride`, which `PageShell`
 * feeds with the page title + active tab) — so it is the one central, translated page name, without
 * the assistant reaching into layout internals. Read at send time rather than memoised with the rest
 * of the context: the title is written in a layout effect, so anything keyed on the route would be a
 * navigation behind. Returns undefined when the title carries no page (the backend then humanizes
 * the path instead).
 */
export const resolveCurrentPageName = (): string | undefined => {
    const title = (typeof document === 'undefined' ? '' : document.title)
        .replace(APP_NAME_SUFFIX, '')
        .trim();
    return title && title !== 'Crqlar' ? title : undefined;
};

const SYSTEM_ROUTE_CONFIG: Record<string, Pick<AssistantRouteMetadata, 'routeName' | 'entityType'>> = {
    availability: {routeName: 'Availability', entityType: 'availability'},
    rates: {routeName: 'Rates', entityType: 'rate'},
    taxRates: {routeName: 'Tax rates', entityType: 'taxRate'},
    resourceCategories: {routeName: 'Resource categories', entityType: 'resourceCategory'},
    resources: {routeName: 'Resources', entityType: 'resource'},
    serviceCategories: {routeName: 'Service categories', entityType: 'serviceCategory'},
    services: {routeName: 'Services', entityType: 'service'},
    ageCategories: {routeName: 'Age categories', entityType: 'ageCategory'},
    printTemplates: {routeName: 'Print templates', entityType: 'printTemplate'},
    profiles: {routeName: 'Profiles', entityType: 'profile'},
    agenda: {routeName: 'Agenda', entityType: 'agenda'},
    deduplication: {routeName: 'Deduplication', entityType: 'deduplication'},
    profileClusters: {routeName: 'Profile clusters', entityType: 'profileCluster'},
    guestAppUsers: {routeName: 'Guest app users', entityType: 'guestAppUser'},
    paymentMethods: {routeName: 'Payment methods', entityType: 'paymentMethod'},
    folios: {routeName: 'Folios', entityType: 'folio'},
    reservations: {routeName: 'Reservations', entityType: 'reservation'},
    correspondenceMessages: {routeName: 'Correspondence messages', entityType: 'correspondenceMessage'},
    contentCategories: {routeName: 'Content categories', entityType: 'contentCategory'},
    contents: {routeName: 'Contents', entityType: 'content'},
    team: {routeName: 'Team', entityType: 'teamMember'},
    organization: {routeName: 'Organization', entityType: 'organization'},
    mappingCodes: {routeName: 'Mapping codes', entityType: 'mappingCode'},
    apiKeys: {routeName: 'API keys', entityType: 'apiKey'},
    sectors: {routeName: 'Sectors', entityType: 'sector'},
    newsletterAccount: {routeName: 'Newsletter account', entityType: 'newsletterAccount'},
    marketingStatistics: {routeName: 'Marketing statistics', entityType: 'marketingStatistics'},
    metaConnector: {routeName: 'Meta connector', entityType: 'metaConnector'},
    transactionalMailSettings: {routeName: 'Transactional mail settings', entityType: 'transactionalMailSettings'},
    forms: {routeName: 'Forms', entityType: 'form'},
};

export const useAssistantContext = (): AssistantContextSnapshot => {
    const location = useLocation();
    const user = useSelector<any, User | undefined>(state => state.auth.user);
    const organization = useSelector<any, Organization | undefined>(state => state.auth.organization);

    return useMemo(
        () => buildAssistantContext(location.pathname, location.search, user, organization),
        [location.pathname, location.search, user, organization],
    );
};

export const buildAssistantContext = (
    currentPath: string,
    search: string,
    user?: User,
    organization?: Organization,
): AssistantContextSnapshot => {
    return {
        currentPath,
        metadata: buildRouteMetadata(currentPath, search),
        user: user
            ? {
                  userType: user.userType,
                  interfaceLanguage: user.interfaceLanguage,
                  permittedAppIds: user.permittedAppIds,
                  billingAccess: user.billingAccess,
                  marketplaceAccess: user.marketplaceAccess,
                  settingsAccess: user.settingsAccess,
              }
            : undefined,
        organization: organization
            ? {
                  id: organization.id,
                  name: organization.name,
                  primaryLanguage: organization.primaryLanguage,
                  supportedLanguages: organization.supportedLanguages,
                  currencyCode: organization.currencyCode,
                  numberOfRooms: organization.numberOfRooms,
              }
            : undefined,
    };
};

const buildRouteMetadata = (currentPath: string, search: string): AssistantRouteMetadata => {
    const segments = currentPath.split('/').filter(Boolean);
    const activeTab = activeTabFromSearch(search);

    if (segments[0] === 'system') {
        const routeKey = segments[1];
        const config = routeKey ? SYSTEM_ROUTE_CONFIG[routeKey] : undefined;
        const routeName = config?.routeName ?? (routeKey ? humanizeSegment(routeKey) : 'Settings');
        const entityType = config?.entityType ?? routeKey;

        return {
            currentPath,
            routeGroup: 'system',
            routeName,
            entityType,
            entityId: firstEntityId(segments.slice(2)),
            isDetailView: segments.length > 2,
            activeTab,
        };
    }

    if (segments[0] === 'bookingEngine') {
        return {
            currentPath,
            routeGroup: 'bookingEngine',
            routeName: 'Booking engine',
            entityType: 'bookingConfiguration',
            entityId: firstEntityId(segments.slice(2)),
            isDetailView: segments.includes('configurations') && segments.length > 2,
            activeTab,
        };
    }

    if (segments[0] === 'marketing') {
        return {
            currentPath,
            routeGroup: 'marketing',
            routeName: humanizeSegment(segments[1] ?? 'Marketing'),
            entityType: segments[1] || 'marketing',
            entityId: firstEntityId(segments.slice(2)),
            isDetailView: segments.length > 2,
            activeTab,
        };
    }

    if (segments[0] === 'crm') {
        return {
            currentPath,
            routeGroup: 'crm',
            routeName: humanizeSegment(segments[1] ?? 'CRM'),
            entityType: segments[1] || 'crm',
            entityId: firstEntityId(segments.slice(2)),
            isDetailView: segments.length > 2,
            activeTab,
        };
    }

    if (segments[0] === 'voiceNote') {
        return {
            currentPath,
            routeGroup: 'voiceNote',
            routeName: 'Voice notes',
            entityType: 'voiceNote',
            entityId: firstEntityId(segments.slice(1)),
            isDetailView: segments.length > 1,
            activeTab,
        };
    }

    if (segments[0]) {
        return {
            currentPath,
            routeGroup: segments[0],
            routeName: humanizeSegment(segments[0]),
            entityType: segments[0],
            entityId: firstEntityId(segments.slice(1)),
            isDetailView: segments.length > 1,
            activeTab,
        };
    }

    return {
        currentPath,
        routeGroup: 'home',
        routeName: 'Home',
        isDetailView: false,
        activeTab,
    };
};

const activeTabFromSearch = (search: string): string | undefined => {
    const params = new URLSearchParams(search);
    return params.get('tab') ?? params.get('view') ?? undefined;
};

const firstEntityId = (segments: string[]): string | undefined => {
    return segments.find(segment => {
        if (!segment || segment === 'new') return false;
        return !['configurations', 'submissions', 'executions', 'edit', 'newService'].includes(segment);
    });
};

const humanizeSegment = (segment: string): string => {
    return segment
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
};
